import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { track } from '@quoter/web-core'
import { db } from '@/lib/db'
import type { Json } from '@/lib/db-types'
import { CHALLENGE_KEYS, parseDays, type ChallengeDay, type ChallengeKey, type ChallengeStatus } from '@/lib/plan'
import { today } from '@/lib/dates'
import { useMe } from './useMe'

const key = (userId: string) => ['lowfodmaps', 'challenges', userId] as const

export type ChallengeView = {
  id: string
  key: ChallengeKey
  status: ChallengeStatus
  startedOn: string
  days: ChallengeDay[]
}

function isChallengeKey(value: string): value is ChallengeKey {
  return (CHALLENGE_KEYS as readonly string[]).includes(value)
}

/** Every challenge the person has started, by FODMAP group. Untested groups are simply absent. */
export function useChallenges() {
  const me = useMe()
  const query = useQuery({
    queryKey: key(me?.userId ?? '__none'),
    enabled: !!me,
    queryFn: async (): Promise<Partial<Record<ChallengeKey, ChallengeView>>> => {
      const { data, error } = await db
        .from('challenges')
        .select('id, fodmap_group, status, started_on, days')
        .eq('organization_id', me!.orgId)
        .eq('user_id', me!.userId)
      if (error) throw error
      const out: Partial<Record<ChallengeKey, ChallengeView>> = {}
      for (const row of data ?? []) {
        if (!isChallengeKey(row.fodmap_group)) continue
        out[row.fodmap_group] = {
          id: row.id,
          key: row.fodmap_group,
          status: row.status,
          startedOn: row.started_on,
          days: parseDays(row.days),
        }
      }
      return out
    },
  })
  return { challenges: query.data ?? {}, loading: query.isLoading, error: query.error }
}

/** Start, update or clear one challenge. All three invalidate the same list. */
export function useChallengeMutations() {
  const me = useMe()
  const queryClient = useQueryClient()
  const refresh = () => {
    if (me) void queryClient.invalidateQueries({ queryKey: key(me.userId) })
  }

  // Events count what happened, never which group or which verdict: that is
  // the person's health, not usage.
  const start = useMutation({
    mutationFn: async (group: ChallengeKey) => {
      if (!me) throw new Error('not_signed_in')
      const { error } = await db.from('challenges').upsert(
        {
          organization_id: me.orgId,
          user_id: me.userId,
          fodmap_group: group,
          status: 'in_progress',
          started_on: today(),
          days: [],
        },
        { onConflict: 'organization_id,user_id,fodmap_group' },
      )
      if (error) throw error
    },
    onSuccess: () => {
      track('challenge_started')
      refresh()
    },
  })

  const update = useMutation({
    mutationFn: async (input: { id: string; days?: ChallengeDay[]; status?: ChallengeStatus }) => {
      if (!me) throw new Error('not_signed_in')
      const { error } = await db
        .from('challenges')
        .update({
          ...(input.days ? { days: input.days as unknown as Json } : {}),
          ...(input.status ? { status: input.status } : {}),
        })
        .eq('id', input.id)
        .eq('organization_id', me.orgId)
        .eq('user_id', me.userId)
      if (error) throw error
    },
    onSuccess: (_data, input) => {
      if (input.status && input.status !== 'in_progress') track('challenge_verdict_chosen')
      refresh()
    },
  })

  const reset = useMutation({
    mutationFn: async (id: string) => {
      if (!me) throw new Error('not_signed_in')
      const { error } = await db
        .from('challenges')
        .delete()
        .eq('id', id)
        .eq('organization_id', me.orgId)
        .eq('user_id', me.userId)
      if (error) throw error
    },
    onSuccess: refresh,
  })

  return { start, update, reset }
}
